import { isNull } from 'lodash-es';

import { LngLat } from '../interfaces';
import { GeofenceType, MarkerType } from './constants';
import { TreadMarkerProps } from './types';

/**
 * A coordinate is safe to pass to Google Maps only when both components are
 * finite. `setCenter` / `fitBounds` throw `InvalidValueError` on NaN and leave
 * the map projection corrupted, which then crashes the deck.gl overlay's next
 * draw with "Cannot read properties of null (reading '0')" (Sentry
 * HORIZON-FE-2XP). Some callers (e.g. the order-form site picker) can produce
 * NaN coordinates for sites that have no lat/lng.
 */
export const isFiniteLngLat = (point?: LngLat | null): point is LngLat =>
  !!point && Number.isFinite(point.lat) && Number.isFinite(point.lng);

/** Drop markers with non-finite coordinates before they reach Google Maps. */
export const filterFiniteMarkers = (markers: TreadMarkerProps[]) =>
  markers.filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng));

type FillColor = 'black' | 'blue' | 'green' | 'purple' | 'orange' | 'red';

/**
 * Color palette for additional geofences in Google Maps rendering (used by modals).
 * Each additional geofence gets a distinct color to differentiate from the primary geofence.
 *
 * NOTE: This uses simple color names for Google Maps Circle/Polygon components.
 * For DeckGL rendering (Live Map), see GEOFENCE_COLORS in transformSiteData.ts which uses
 * precise RGBA values from the brandV2 theme palette for tag-based coloring
 * (loading=green, unloading=orange, check_in=blue, scaling=yellow, ticketing=red).
 *
 * The difference is intentional:
 * - Modal views (Edit Site, Order details): Simple cycling colors for quick visual differentiation
 * - Live Map (DeckGL): Semantic tag-based colors matching brand guidelines
 */
export const ADDITIONAL_GEOFENCE_COLORS: FillColor[] = [
  'green',
  'purple',
  'orange',
  'red',
];

interface CircleShape {
  center: { lng: number; lat: number };
  radius: number;
  fillColor: FillColor;
  isDefaultSite?: boolean;
}

interface PolygonShape {
  id: string;
  paths: { lat: number; lng: number }[];
  fillColor: FillColor;
  isDefaultSite?: boolean;
}

const isSiteMarkerType = (type: MarkerType): boolean =>
  type === MarkerType.site ||
  type === MarkerType.pickup_site ||
  type === MarkerType.dropoff_site ||
  type === MarkerType.staging_site ||
  type === MarkerType.both_site;

interface BuildCirclesParams {
  markers: TreadMarkerProps[];
  /** True when DeckGL renders static sites (Live Map); false in modals. */
  useDeckGLForSites: boolean;
  editableCircleCenter: LngLat | null;
  editableCircleRadius: number | null;
  /** Whether an onEditableCircleChange handler was provided. */
  hasEditableCircleChange: boolean;
  editableCircleReplacesPrimaryMarker: boolean;
  selectedCircleMarkerId: string | null;
}

/**
 * Extract circles for Google Maps Circle rendering.
 * - When using DeckGL (Live Map): Only render moving_site circles
 * - When not using DeckGL (modals): Render all site circles
 * - Additional geofences get distinct colors for visual differentiation
 */
export function buildCircles({
  markers,
  useDeckGLForSites,
  editableCircleCenter,
  editableCircleRadius,
  hasEditableCircleChange,
  editableCircleReplacesPrimaryMarker,
  selectedCircleMarkerId,
}: BuildCirclesParams): CircleShape[] {
  return markers.reduce((acc, marker) => {
    // Moving sites always use Google Maps Circle for real-time position updates
    if (marker.type === MarkerType.moving_site) {
      const newCircles = marker.radii.map((radius) => ({
        center: {
          lng: marker.circleCenter.lng,
          lat: marker.circleCenter.lat,
        },
        radius,
        fillColor: 'black' as FillColor,
        isDefaultSite: false,
      }));
      return [...acc, ...newCircles];
    }

    // Static site circles: only render via Google Maps when NOT using DeckGL
    // Exception: geofence-location-marker is always rendered via Google Maps for configuration preview
    const isSiteMarker = isSiteMarkerType(marker.type);

    // Site form and Job Geo Debug: skip primary circle only when editable circle replaces it (primary selected)
    const isGeofenceConfigMarker =
      marker.id === 'geofence-location-marker' || marker.id === 'lat-lon-circle-marker';
    const skipAsRegularCircle =
      isGeofenceConfigMarker &&
      !isNull(editableCircleCenter) &&
      !isNull(editableCircleRadius) &&
      hasEditableCircleChange &&
      editableCircleReplacesPrimaryMarker;

    if (
      (!useDeckGLForSites || isGeofenceConfigMarker) &&
      isSiteMarker &&
      'geofenceType' in marker &&
      !skipAsRegularCircle
    ) {
      if (marker.geofenceType === GeofenceType.circle && 'radii' in marker) {
        const circleMarker = marker as {
          id: string;
          radii: number[];
          circleCenter: { lat: number; lng: number };
          lat: number;
          lng: number;
          isAdditionalGeofence?: boolean;
          additionalGeofenceIndex?: number;
        };
        const fillColor = !isNull(selectedCircleMarkerId)
          ? circleMarker.id === selectedCircleMarkerId
            ? circleMarker.id === 'lat-lon-circle-marker'
              ? ('blue' as FillColor)
              : ('orange' as FillColor)
            : ('blue' as FillColor)
          : isGeofenceConfigMarker
            ? ('orange' as FillColor)
            : (circleMarker.isAdditionalGeofence ?? false)
              ? ADDITIONAL_GEOFENCE_COLORS[
                  (circleMarker.additionalGeofenceIndex ?? 0) %
                    ADDITIONAL_GEOFENCE_COLORS.length
                ]
              : ('blue' as FillColor);

        const isDefaultSite = 'isDefaultSite' in marker && Boolean(marker.isDefaultSite);
        const newCircles = circleMarker.radii.map((radius) => ({
          center: {
            lng: circleMarker.circleCenter?.lng ?? circleMarker.lng,
            lat: circleMarker.circleCenter?.lat ?? circleMarker.lat,
          },
          radius,
          fillColor,
          isDefaultSite,
        }));
        return [...acc, ...newCircles];
      }
    }

    return acc;
  }, [] as CircleShape[]);
}

interface BuildPolygonsParams {
  markers: TreadMarkerProps[];
  /** True when DeckGL renders static sites (Live Map); false in modals. */
  useDeckGLForSites: boolean;
  selectedCircleMarkerId: string | null;
}

/**
 * Extract polygons for Google Maps Polygon rendering.
 * Only rendered when NOT using DeckGL (for Order/Site modals).
 * For Edit Site Modal: renders all additional polygon geofences from coordinates array.
 * For Order modals: additional geofences get distinct colors for visual differentiation.
 */
export function buildPolygons({
  markers,
  useDeckGLForSites,
  selectedCircleMarkerId,
}: BuildPolygonsParams): PolygonShape[] {
  if (useDeckGLForSites) {
    return [];
  }

  const result: PolygonShape[] = [];

  markers.forEach((marker) => {
    const isSiteMarker = isSiteMarkerType(marker.type);

    if (isSiteMarker && 'geofenceType' in marker) {
      if (marker.geofenceType === GeofenceType.polygon && 'coordinates' in marker) {
        const polygonMarker = marker as {
          id: string;
          coordinates: [number, number][][];
          isAdditionalGeofence?: boolean;
          additionalGeofenceIndex?: number;
        };
        if (polygonMarker.coordinates && polygonMarker.coordinates.length > 0) {
          const fillColor = !isNull(selectedCircleMarkerId)
            ? ('blue' as FillColor)
            : (polygonMarker.isAdditionalGeofence ?? false)
              ? ADDITIONAL_GEOFENCE_COLORS[
                  (polygonMarker.additionalGeofenceIndex ?? 0) %
                    ADDITIONAL_GEOFENCE_COLORS.length
                ]
              : ('blue' as FillColor);

          const isDefaultSite =
            'isDefaultSite' in marker && Boolean(marker.isDefaultSite);
          // Iterate over ALL polygon coordinates (Edit Site Modal passes multiple)
          polygonMarker.coordinates.forEach((polygon, polyIndex) => {
            if (polygon && polygon.length >= 3) {
              result.push({
                id: `${polygonMarker.id}-${polyIndex}`,
                paths: polygon.map(([lng, lat]) => ({ lat, lng })),
                fillColor,
                isDefaultSite,
              });
            }
          });
        }
      }
    }
  });

  return result;
}
